import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Briefcase, Activity, DollarSign, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { PageHeader, DataTable, Column, MetricCard, KPIGrid } from '@/components/common';
import { jobsApi, JOB_STATUSES, JOB_TYPES, type Job } from '@/api/services/jobs.api';
import { getErrorMessage } from '@/api/client';
import { formatCurrency, toTitleCase } from '@/lib/utils';
import { WORK_UNITS } from '@/lib/work-units';
import { JOB_STATUS_TONE } from '@/lib/job-status';

const emptyForm = {
  jobNumber: '',
  name: '',
  status: 'PLANNED',
  jobType: 'DRILLING',
  customerName: '',
  afeNumber: '',
  wellName: '',
  wellNumber: '',
  apiNumber: '',
  leaseName: '',
  fieldName: '',
  rigName: '',
  plannedBaseMetric: '',
  baseMetricUnit: 'ft',
  // Tri-state on purpose. '' is nobody having said, which readiness treats as
  // unknown rather than as no — the distinction the two gates turn on.
  permitsRequired: '' as '' | 'yes' | 'no',
  tubularsRequired: '' as '' | 'yes' | 'no',
};

export default function Jobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await jobsApi.getAll({
        limit: 100,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
      });
      setJobs(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search, statusFilter, load]);

  const openJobs = jobs.filter((j) => j.status === 'PLANNED' || j.status === 'ACTIVE');
  const consumedTotal = jobs.reduce((sum, j) => sum + (j.consumedCost ?? 0), 0);
  const withAfe = jobs.filter((j) => j.afeNumber).length;

  const handleCreate = async () => {
    if (!form.jobNumber || !form.name) {
      toast.error('A job number and name are required');
      return;
    }
    try {
      setSaving(true);
      const payload: Record<string, unknown> = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '')
      );
      // The form holds every field as a string; this one is a number server-side.
      if (payload.plannedBaseMetric) payload.plannedBaseMetric = Number(payload.plannedBaseMetric);
      else delete payload.baseMetricUnit;
      // 'yes'/'no' become booleans; unanswered was already dropped by the
      // empty-string filter above, so it stays null and reads as unknown.
      for (const key of ['permitsRequired', 'tubularsRequired'] as const) {
        if (payload[key] !== undefined) payload[key] = payload[key] === 'yes';
      }
      await jobsApi.create(payload as never);
      toast.success(`Job ${form.jobNumber} created`);
      setFormOpen(false);
      setForm(emptyForm);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<Job>[] = [
    {
      key: 'jobNumber',
      header: 'Job',
      sortable: true,
      linkTo: (job) => `/jobs/${job.id}`,
      render: (job) => (
        <div>
          <p className="font-medium">{job.jobNumber}</p>
          <p className="text-xs leading-4 text-muted-foreground">{job.name}</p>
        </div>
      ),
    },
    {
      key: 'customerName',
      header: 'Customer',
      render: (job) => (
        <span className="text-sm">
          {job.customerName || <span className="text-muted-foreground">—</span>}
        </span>
      ),
    },
    {
      key: 'well',
      header: 'Well',
      render: (job) => (
        <div className="text-sm">
          <p>{job.well?.name || <span className="text-muted-foreground">—</span>}</p>
          {job.well && (
            <p className="font-mono text-xs text-muted-foreground">API {job.well.apiNumber}</p>
          )}
        </div>
      ),
    },
    {
      key: 'afeNumber',
      header: 'AFE',
      render: (job) => (
        <span className="font-mono text-sm">
          {job.afeNumber || <span className="font-sans text-muted-foreground">—</span>}
        </span>
      ),
    },
    {
      key: 'jobType',
      header: 'Type',
      render: (job) => (
        <span className="text-sm">{job.jobType ? toTitleCase(job.jobType) : '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (job) => (
        <Badge className={JOB_STATUS_TONE[job.status] ?? ''}>{toTitleCase(job.status)}</Badge>
      ),
    },
    {
      key: 'consumedCost',
      header: 'Consumed',
      sortable: true,
      accessorFn: (job) => job.consumedCost ?? 0,
      render: (job) => (
        <div className="text-right">
          <p className="font-medium tabular-nums">{formatCurrency(job.consumedCost ?? 0)}</p>
          <p className="text-xs text-muted-foreground">
            {job._count?.consumptionRecords ?? 0} record
            {(job._count?.consumptionRecords ?? 0) === 1 ? '' : 's'}
          </p>
        </div>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Inventory', href: '/inventory' },
            { label: 'Jobs' },
          ]}
          title="Jobs"
          description="Field work that material and labour are charged to. Each job carries the customer references used to rebill its charges."
          actions={
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Job
            </Button>
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Jobs"
            value={jobs.length}
            sentiment="info"
            icon={<Briefcase className="h-5 w-5" />}
          />
          <MetricCard
            label="Open"
            value={openJobs.length}
            sentiment="success"
            subtitle="planned or active"
            icon={<Activity className="h-5 w-5" />}
          />
          <MetricCard
            label="Consumed"
            value={formatCurrency(consumedTotal)}
            sentiment="info"
            subtitle="material charged to date"
            icon={<DollarSign className="h-5 w-5" />}
          />
          <MetricCard
            label="With an AFE"
            value={`${withAfe} of ${jobs.length}`}
            sentiment={withAfe === jobs.length ? 'success' : 'warning'}
            subtitle="rebillable against a customer authorisation"
            icon={<TrendingUp className="h-5 w-5" />}
          />
        </KPIGrid>
      </motion.div>

      <motion.div variants={fadeUp}>
        <DataTable
          data={jobs}
          columns={columns}
          loading={loading}
          toolbar={
            <>
              <Input
                placeholder="Search job, well, AFE or customer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm"
              />
              <Select
                value={statusFilter || 'all'}
                onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {JOB_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {toTitleCase(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          }
          onRowClick={(job) => navigate(`/jobs/${job.id}`)}
          emptyTitle="No jobs yet"
          emptyDescription="A job is what material gets charged to. Create one before recording consumption."
          emptyAction={
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Job
            </Button>
          }
          pagination
          exportable
          columnToggle
        />
      </motion.div>

      {/* Create */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>New job</SheetTitle>
            <SheetDescription>
              The customer references recorded here travel with every consumption charged to this
              job, so they are entered once rather than per record.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jobNumber">Job number *</Label>
                <Input
                  id="jobNumber"
                  value={form.jobNumber}
                  onChange={(e) => setForm((f) => ({ ...f, jobNumber: e.target.value }))}
                  placeholder="JOB-2026-0042"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jobType">Type</Label>
                <Select
                  value={form.jobType}
                  onValueChange={(v) => setForm((f) => ({ ...f, jobType: v }))}
                >
                  <SelectTrigger id="jobType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {toTitleCase(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Permian 14-3H completion"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {JOB_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {toTitleCase(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 border-t pt-4">
              <h3 className="text-base font-semibold">Planned work</h3>
              <p className="text-sm text-muted-foreground">
                What this job is expected to do. Multiplied by the consumption rates already
                observed for an item, this is what turns history into an expectation for a well that
                has not started.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plannedBaseMetric">Quantity of work</Label>
                  <Input
                    id="plannedBaseMetric"
                    type="number"
                    min={0}
                    value={form.plannedBaseMetric}
                    onChange={(e) => setForm((f) => ({ ...f, plannedBaseMetric: e.target.value }))}
                    placeholder="5000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="baseMetricUnit">Unit</Label>
                  <Select
                    value={form.baseMetricUnit}
                    onValueChange={(v) => setForm((f) => ({ ...f, baseMetricUnit: v }))}
                  >
                    <SelectTrigger id="baseMetricUnit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WORK_UNITS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h3 className="text-base font-semibold">Customer references</h3>
              <p className="text-sm text-muted-foreground">
                What the charge is rebilled against. The AFE is the customer&rsquo;s authorisation,
                not the service company&rsquo;s.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {(
                  [
                    ['customerName', 'Customer', 'Apex Operating'],
                    ['afeNumber', 'AFE number', 'AFE-2026-0417'],
                    ['wellName', 'Well name', 'Permian 14-3H'],
                    ['wellNumber', 'Well number', '14-3H'],
                    ['apiNumber', 'API number', '42-329-38217'],
                    ['rigName', 'Rig', 'Rig 7'],
                    ['leaseName', 'Lease', 'Sundown'],
                    ['fieldName', 'Field', 'Midland Basin'],
                  ] as const
                ).map(([key, label, placeholder]) => (
                  <div key={key} className="space-y-2">
                    <Label htmlFor={key}>{label}</Label>
                    <Input
                      id={key}
                      value={form[key]}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-medium">What this job needs</h3>
              {/* Both feed a readiness gate, and both were unreachable: the
                  model carried permitsRequired and nothing but the seed could
                  write it, so a job created here left its permit gate unassessed
                  for ever. Left blank they stay unknown, which is honest and is
                  what the gates already do with it. */}
              <p className="text-sm text-muted-foreground">
                Readiness cannot tell &ldquo;not needed&rdquo; from &ldquo;not checked&rdquo;.
                Answering here is what lets those gates clear.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {(
                  [
                    ['permitsRequired', 'Needs a permit'],
                    ['tubularsRequired', 'Runs casing or tubing'],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key} className="space-y-2">
                    <Label htmlFor={key}>{label}</Label>
                    <Select
                      value={form[key] || 'unknown'}
                      onValueChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          [key]: v === 'unknown' ? '' : (v as 'yes' | 'no'),
                        }))
                      }
                    >
                      <SelectTrigger id={key}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unknown">Not established</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t pt-4">
              <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating…' : 'Create job'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </motion.main>
  );
}
